/* eslint-disable @typescript-eslint/no-explicit-any */

import type { AccData } from '@/app/common/memsearch/typesAccData.ts';
import type { TranslationFunction } from '@/app/components/translations/Translation';
import { type DateItem, type Dates, DatesType, DateType } from '../utils';
import { formatDateShortDayMonth, formatDateShortMonthYear } from '../utils/dates/utilDates';
import type { Localization } from './Localization';

type GetFieldValue = {
  transformDate?: any;
  language: string;
  country: string;
  datesType?: DatesType;
  locale: string;
  t: TranslationFunction;
};

type DecorateDatesParams = {
  date: DateItem;
  language: string;
  country: string;
  datesType?: DatesType;
  locale: string;
  t: TranslationFunction;
};

const decorateAnytime = (t: TranslationFunction): string =>
  t('vacation.oss.header.widget.calendar.anytime').toLowerCase();
const decorateWeekend = (t: TranslationFunction): string => t('vacation.oss.header.widget.calendar.weekend');

interface DecorateTimeParams {
  daysFrom?: number;
  daysTo?: number;
  t: TranslationFunction;
}

const decorateTime = ({ daysFrom, daysTo, t }: DecorateTimeParams): string => {
  let tval = `${daysFrom} - ${daysTo} ${t('vacation.oss.header.widget.calendar.nights')}`;

  if (daysTo) {
    if (daysTo === 1) {
      tval = `${daysTo} ${t('vacation.oss.header.widget.calendar.night')}`;
    } else if (daysTo > 1 && daysFrom === daysTo) {
      tval = `${daysTo} ${t('vacation.oss.header.widget.calendar.nights')}`;
    } else {
      tval = `${daysFrom}-${daysTo} ${t('vacation.oss.header.widget.calendar.nights')}`;
    }
  }

  return tval;
};

type DecorateMonthsParams = {
  months: string[];
  language: string;
  locale: string;
};

const decorateMonths = ({ months: yearMonthList, locale }: DecorateMonthsParams): string =>
  yearMonthList.map((yearMonth) => `${formatDateShortMonthYear(locale, `${yearMonth}-01`)}`).join(', ');

export const decorateDates = (params: DecorateDatesParams): string => {
  const { date, t, language, datesType, locale } = params;
  const { type, dateFrom, daysTo, daysFrom, months } = date;

  if (date !== null && type === DateType.FLEX && !dateFrom) {
    return '';
  }

  switch (type) {
    case DateType.ANYTIME: {
      const anyTimeDecorated = decorateAnytime(t);
      if (datesType === DatesType.FLEXIBLE) {
        const timeDecorated =
          date.subType === DateType.TIME ? decorateTime({ daysFrom, daysTo, t }) : decorateWeekend(t);
        return `${timeDecorated} ${anyTimeDecorated}`;
      } else {
        return decorateAnytime(t);
      }
    }
    case DateType.EXACT:
      return formatDateShortDayMonth(locale, date?.date || '');
    case DateType.MONTHS:
      if (months) {
        const timeDecorated =
          date.subType === DateType.TIME ? decorateTime({ daysFrom, daysTo, t }) : decorateWeekend(t);
        const monthsDecorated = decorateMonths({ months, language, locale });
        return `${timeDecorated} ${t('vacation.oss.header.widget.calendar.in')} ${monthsDecorated}`;
      }
      break;
    default:
      return '';
  }

  return '';
};

export const getFieldValue = ({ transformDate, language, country, datesType, locale, t }: GetFieldValue): string => {
  const date = transformDate;
  let input = '';
  const isTime = date && date.type === DateType.TIME;
  const isMonths = date && date.type === DateType.MONTHS;
  const isAnytime = date && date.type === DateType.ANYTIME;
  if (
    !date ||
    (!isTime && !isMonths && !isAnytime && (!date.date || date.date === '')) ||
    (isTime && (!date.daysfrom || date.daysfrom === '')) ||
    (isMonths && date.months.length === 0)
  ) {
    return '';
  } else {
    input = decorateDates({ date, t, language, country, datesType, locale });
  }

  return input;
};

export const getDatesAsLabel = (
  searchDates: Dates | undefined,
  localization: Localization,
  accData: AccData | undefined,
  t: TranslationFunction
) => {
  const { locale, country, lang } = localization;
  let dates = '';

  if (searchDates?.type === DatesType.FLEXIBLE) {
    dates = getFieldValue({
      transformDate: searchDates.flexDate,
      language: lang,
      country,
      t,
      datesType: searchDates.type,
      locale,
    });
  } else {
    let checkinDate = searchDates?.departureDate;
    if (checkinDate && accData?.checkin) {
      checkinDate = { ...checkinDate, date: accData?.checkin };
    }
    let checkoutDate = searchDates?.returnDate;
    if (checkoutDate && accData?.checkout) {
      checkoutDate = { ...checkoutDate, date: accData?.checkout };
    }

    const departureDates = getFieldValue({
      transformDate: checkinDate,
      language: lang,
      country,
      t,
      locale,
    });
    const returnDates = getFieldValue({
      transformDate: checkoutDate,
      language: lang,
      country,
      t,
      locale,
    });
    const flexibleDates = searchDates?.subDate ? ` (±${searchDates.subDate})` : '';
    dates = `${departureDates} - ${returnDates}${flexibleDates}`;
  }

  return dates;
};

type GetSubTitle = {
  searchDates: Dates;
  t: TranslationFunction;
  dealsCount: number;
  isMobile: boolean;
  showCustomCheckinCheckout: boolean;
  accData?: AccData;
  localization: Localization;
};
const getSubTitle = ({
  searchDates,
  t,
  dealsCount,
  isMobile,
  showCustomCheckinCheckout,
  accData,
  localization,
}: GetSubTitle) => {
  const dates = getDatesAsLabel(searchDates, localization, accData, t);
  const customCheckinCheckoutDates = isMobile ? '' : `<b>${dates}</b>`;

  const MAX = 300;
  const count = dealsCount ? dealsCount.toString() : '0';
  const total = dealsCount >= MAX ? '300' : count;

  if (showCustomCheckinCheckout) {
    return t('vacation.oss.indexcollapsed.subtitle', {
      total,
      dates: customCheckinCheckoutDates,
    });
  }

  if (total === '0') {
    return t('vacation.oss.title.stay_found', {
      amount: total,
      dates,
    });
  }

  if (total === '1') {
    return t('vacation.oss.title.stay_found_sing', {
      amount: total,
      dates,
    });
  }

  if (total === '300') {
    return t('vacation.oss.title.over_stay_found', {
      amount: total,
      dates,
    });
  }

  return t('vacation.oss.title.stay_found', {
    amount: total,
    dates,
  });
};

export default getSubTitle;
